import {
  StaffMember,
  StaffRoles,
  StaffStates,
  isStaffAdmin,
  isStaffManager,
} from '@/modules/StaffManagement/models';
import { reactive } from '@nuxtjs/composition-api';
import { orderBy } from 'lodash';

export enum FilterTypes {
  STATUS = 'Status',
  ROLE = 'Role',
}
export const SortingKey = 'SortBy';

interface Filters {
  [FilterTypes.ROLE]: String | null;
  [FilterTypes.STATUS]: String | null;
  [SortingKey]: String | null;
}

const defaultState = Object.freeze({
  [FilterTypes.ROLE]: null,
  [FilterTypes.STATUS]: null,
  [SortingKey]: null,
});

const stateOptions = Object.keys(StaffStates).map((o) => StaffStates[o]);
// Intentionally excluding "Admin" and "Approver" roles here...
const roleOptions = [
  StaffRoles.EMPLOYEE,
  StaffRoles.MANAGER,
  StaffRoles.CONTRACTOR,
];

const staffFilters = [
  {
    label: FilterTypes.STATUS,
    name: FilterTypes.STATUS,
    options: stateOptions,
    placeholder: 'Current',
  },
  {
    label: FilterTypes.ROLE,
    name: FilterTypes.ROLE,
    options: roleOptions,
    placeholder: 'Any',
  },
];

export enum SortingTypes {
  ALPHA_ASC = 'A - Z',
  ALPHA_DESC = 'Z - A',
  ROLE_DESC = 'Role: Admin First',
  ROLE_ASC = 'Role: Employee First',
}

const sortOptions = [
  SortingTypes.ALPHA_ASC,
  SortingTypes.ALPHA_DESC,
  SortingTypes.ROLE_DESC,
  SortingTypes.ROLE_ASC,
];

// Role priority for sorting (higher number = higher role)
// Note: isStaffManager already excludes admins (admin supersedes manager)
const getRolePriority = (staff: StaffMember): number => {
  if (isStaffAdmin(staff)) {
    return 3;
  }
  if (isStaffManager(staff)) {
    return 2;
  }

  return 1; // Employee/Contractor
};

const state = reactive<Filters>({ ...defaultState });

const setFilter = (type: FilterTypes, value: string | null) => {
  state[type] = value;
};

const setSorting = (value: string | null) => {
  state[SortingKey] = value;
};

const getState = () => state;

const setState = (newState) => {
  Object.assign(state, { ...newState });
};

const resetFilters = () => {
  Object.assign(state, { ...defaultState });
};

const filterAndSortStaff = (staffList: StaffMember[]) => {
  let staffArr: StaffMember[] = staffList;

  if (state[FilterTypes.ROLE]) {
    switch (state[FilterTypes.ROLE]) {
      case StaffRoles.MANAGER:
        // Use helper which excludes admins (admin supersedes manager)
        staffArr = staffList.filter((m: StaffMember) => isStaffManager(m));
        break;
      case StaffRoles.ADMIN:
        staffArr = staffList.filter((m: StaffMember) => isStaffAdmin(m));
        break;
      case StaffRoles.APPROVER:
        staffArr = staffList.filter((m: StaffMember) =>
          m.roles.includes(StaffRoles.APPROVER)
        );
        break;
      case StaffRoles.CONTRACTOR:
        staffArr = staffList.filter((m: StaffMember) => m.contractor);
        break;
      case StaffRoles.EMPLOYEE:
      default:
        // Exclude admins, managers, and contractors
        staffArr = staffList.filter(
          (m: StaffMember) =>
            !isStaffAdmin(m) && !isStaffManager(m) && !m.contractor
        );
        break;
    }
  }

  // Filter by Staff Status
  if (state[FilterTypes.STATUS]) {
    staffArr = staffArr.filter(
      (m: StaffMember) => m.staffState === state[FilterTypes.STATUS]?.toString()
    );
  } else {
    // if not set (e.g. all statuses), filter out former employees
    staffArr = staffArr.filter(
      (m: StaffMember) => m.staffState !== StaffStates.FORMER
    );
  }

  // Sort By
  if (state[SortingKey]) {
    switch (state[SortingKey]) {
      case SortingTypes.ALPHA_ASC:
        staffArr = orderBy(
          staffArr,
          (m: StaffMember) => m.profile.staffName.toLowerCase(),
          ['asc']
        );
        break;
      case SortingTypes.ALPHA_DESC:
        staffArr = orderBy(
          staffArr,
          (m: StaffMember) => m.profile.staffName.toLowerCase(),
          ['desc']
        );
        break;
      case SortingTypes.ROLE_DESC:
        staffArr = orderBy(
          staffArr,
          [
            (m: StaffMember) => getRolePriority(m),
            (m: StaffMember) => m.profile.staffName.toLowerCase(),
          ],
          ['desc', 'asc']
        );
        break;
      case SortingTypes.ROLE_ASC:
        staffArr = orderBy(
          staffArr,
          [
            (m: StaffMember) => getRolePriority(m),
            (m: StaffMember) => m.profile.staffName.toLowerCase(),
          ],
          ['asc', 'asc']
        );
        break;
    }
  }

  return staffArr;
};

const useFilter = () => {
  return {
    currentFilters: state,
    getState,
    filterAndSortStaff,
    resetFilters,
    setFilter,
    setSorting,
    setState,
    sortOptions,
    staffFilters,
  };
};

export default useFilter;
